import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { useCustomer } from '../../context/CustomerContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AccountRegister() {
  const { login } = useCustomer();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', confirm: '', first_name: '', last_name: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/customer/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email, password: form.password,
          first_name: form.first_name, last_name: form.last_name, phone: form.phone,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      login(data.token, data.customer);
      navigate('/account');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600";
  const labelCls = "block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield size={24} className="text-orange-500" />
            <span className="font-bebas text-2xl text-white tracking-widest">WITT MACHINE</span>
          </div>
          <h1 className="font-bebas text-4xl text-white tracking-wider mb-1">CREATE ACCOUNT</h1>
          <p className="font-inter text-sm text-zinc-500">Track orders, save your FFL dealer, order faster next time.</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>First Name *</label>
              <input data-testid="reg-first" type="text" required value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="John" />
            </div>
            <div>
              <label className={labelCls}>Last Name *</label>
              <input data-testid="reg-last" type="text" required value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="Doe" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email Address *</label>
            <input data-testid="reg-email" type="email" required value={form.email} onChange={set('email')} className={inputCls} placeholder="you@example.com" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="reg-phone" type="tel" value={form.phone} onChange={set('phone')} className={inputCls} placeholder="(555) 555-5555" />
          </div>
          <div>
            <label className={labelCls}>Password * (min 8 characters)</label>
            <div className="relative">
              <input data-testid="reg-password" type={showPwd ? 'text' : 'password'} required
                value={form.password} onChange={set('password')} className={`${inputCls} pr-10`} placeholder="••••••••" />
              <button type="button" onClick={() => setShowPwd(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Confirm Password *</label>
            <input data-testid="reg-confirm" type="password" required value={form.confirm} onChange={set('confirm')} className={inputCls} placeholder="••••••••" />
          </div>

          <div className="bg-[#1a1a1a] border border-white/5 p-3">
            <p className="font-inter text-xs text-zinc-500 leading-relaxed">
              By creating an account you agree to our{' '}
              <a href="/resources" className="text-orange-400 hover:text-orange-300">Terms & Conditions</a>.
              Your account lets you track suppressors through production, view serial numbers when assigned,
              and see tracking once shipped.
            </p>
          </div>

          <button data-testid="reg-submit" type="submit" disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-4 transition-colors">
            {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <p className="font-inter text-sm text-zinc-500">
            Already have an account?{' '}
            <Link to="/account/login" className="text-orange-400 hover:text-orange-300 transition-colors">Sign in →</Link>
          </p>
          <p className="font-inter text-xs text-zinc-600">
            <Link to="/" className="hover:text-zinc-400 transition-colors">← Back to store</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
